import Head from 'next/head';
import Link from 'next/link';
import Header from '../../components/Header';

export default function WaterHeaterReplacementPage() {
  const phoneNumber = "(630) 715-8829";
  const companyName = "Partners Plumbing Co.";
  const serviceName = "Water Heater Replacement";
  const serviceArea = "Kane County, Geneva, Saint Charles, South Elgin";

  const replacementServices = [
    {
      title: "Tank Water Heater Replacement",
      description: "Complete replacement of traditional tank water heaters with energy-efficient models",
      features: ["40-80 gallon capacity", "10+ year warranties", "Energy Star certified", "Same-day installation"]
    },
    {
      title: "Tankless Water Heater Installation",
      description: "Modern tankless water heater installation for endless hot water and energy savings",
      features: ["Endless hot water", "Up to 30% energy savings", "Compact design", "20+ year lifespan"]
    },
    {
      title: "Hybrid Water Heater Systems",
      description: "High-efficiency hybrid heat pump water heaters for maximum energy savings",
      features: ["Heat pump technology", "Up to 70% energy savings", "Environmental incentives", "Smart controls"]
    },
    {
      title: "Emergency Replacement",
      description: "Emergency water heater replacement when your current unit fails completely",
      features: ["Same-day service", "Emergency installation", "Temporary hot water solutions", "Insurance assistance"]
    }
  ];

  const replacementSigns = [
    "Water heater is over 10 years old",
    "Frequent repairs needed",
    "Rusty or discolored hot water",
    "Strange noises from the unit",
    "Leaking around the tank",
    "Inconsistent water temperature",
    "Higher energy bills",
    "Not enough hot water for household needs"
  ];

  const heaterTypes = [
    {
      type: "Traditional Tank",
      description: "Reliable and affordable option for most homes",
      pros: ["Lower upfront cost", "Easy maintenance", "Proven technology"],
      bestFor: "Budget-conscious homeowners with standard hot water needs"
    },
    {
      type: "Tankless",
      description: "On-demand hot water with space-saving design",
      pros: ["Endless hot water", "Energy efficient", "Space saving"],
      bestFor: "Homes with limited space or high hot water demand"
    },
    {
      type: "Hybrid Heat Pump",
      description: "Most energy-efficient option with smart technology",
      pros: ["Maximum efficiency", "Smart controls", "Rebates available"],
      bestFor: "Environmentally conscious homeowners seeking maximum savings"
    }
  ];

  const faqItems = [
    {
      question: "How do I know when to replace my water heater?",
      answer: "Consider replacement if your water heater is over 10 years old, requires frequent repairs, produces rusty water, or can't meet your household's hot water demands. Our technicians can assess your unit and recommend the best solution."
    },
    {
      question: "What size water heater do I need?",
      answer: "Water heater size depends on your household size and usage patterns. Generally, a family of 4 needs a 40-50 gallon tank or an 8-10 GPM tankless unit. We'll calculate your specific needs during our assessment."
    },
    {
      question: "How long does water heater installation take?",
      answer: "Most water heater replacements take 3-6 hours, depending on the type and any necessary modifications. We often complete same-day installations when you call before noon."
    },
    {
      question: "Are there rebates available for energy-efficient water heaters?",
      answer: "Yes, many utility companies and government programs offer rebates for energy-efficient water heaters. We'll help you identify and apply for available incentives to reduce your installation cost."
    }
  ];

  return (
    <>
      <Head>
        <title>{`Water Heater Repair Near Me | Geneva & Kane County IL | ${companyName}`}</title>
        <meta name="description" content={`Water heater repair near me in Geneva, St Charles, South Elgin & Kane County, IL. Water heater replacement, tankless water heater installation. Same-day service. Call ${phoneNumber} now!`} />
        <meta name="keywords" content="water heater repair near me, water heater replacement, water heater Kane County, tankless water heater Geneva IL, water heater not working Kane County, water heater installation near me, water heater repair Geneva, water heater St Charles IL" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://partnersinplumbing.com/services/water-heater-replacement" />
        <link rel="icon" href="/favicon.ico" />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": companyName,
            "description": `Professional water heater replacement services in ${serviceArea}`,
            "telephone": phoneNumber,
            "serviceArea": serviceArea,
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "Illinois",
              "addressLocality": "Kane County",
              "postalCode": "60177"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Water Heater Replacement Services",
              "itemListElement": replacementServices.map(service => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": service.title,
                  "description": service.description
                }
              }))
            }
          })}
        </script>
      </Head>

      <div className="min-h-screen bg-white">
        <Header currentPage="/services/water-heater-replacement" phoneNumber={phoneNumber} companyName={companyName} />

        {/* Sticky Contact Button */}
        <div className="fixed bottom-6 right-6 z-50">
          <Link 
            href="/contact" 
            className="bg-orange-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-orange-700 transition-colors font-bold text-lg animate-pulse"
          >
            Get Quote
          </Link>
        </div>

        {/* Hero Section */}
        <section className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Water Heater Repair Near Me - Geneva, IL | Serving Kane County
              </h1>
              <p className="text-xl md:text-2xl text-slate-100 mb-8 max-w-3xl mx-auto">
                Water heater repair near me in Geneva, St Charles, South Elgin & Kane County, IL. Water heater replacement and tankless water heater installation. Same-day service available.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <a 
                  href={`tel:${phoneNumber}`} 
                  className="bg-yellow-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
                >
                  Call Now: {phoneNumber}
                </a>
                <Link 
                  href="/contact" 
                  className="bg-white text-slate-800 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
                >
                  Get Free Quote Online
                </Link>
              </div>
              <p className="text-slate-200 text-sm">
                🔥 Same-day installation | 💰 Free estimates | 🏆 10+ year warranties available
              </p>
            </div>
          </div>
        </section>

        {/* Replacement Signs */}
        <section className="py-16 bg-orange-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Signs You Need Water Heater Replacement</h2>
              <p className="text-xl text-gray-600">Don't wait for complete failure - plan your replacement</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {replacementSigns.map((sign, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-orange-500 rounded-full flex-shrink-0 mr-3"></div>
                    <p className="text-gray-900 font-medium text-sm">{sign}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <p className="text-lg text-gray-700 mb-4">Planning ahead saves money and prevents emergencies! If your water heater has already failed, our <Link href="/services/emergency-plumbing-service" className="text-orange-600 hover:text-orange-800 font-semibold underline">emergency plumbing service in Kane County</Link> offers same-day replacement.</p>
              <a href={`tel:${phoneNumber}`} className="bg-orange-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-orange-700 transition-colors">
                Schedule Assessment: {phoneNumber}
              </a>
            </div>
          </div>
        </section>

        {/* Water Heater Types */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Choose the Right Water Heater</h2>
              <p className="text-xl text-gray-600">We install all types of water heaters to meet your needs and budget</p>
            </div>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {heaterTypes.map((type, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{type.type} Water Heater</h3>
                  <p className="text-gray-600 mb-4">{type.description}</p>
                  
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">Advantages:</h4>
                    <ul className="space-y-1">
                      {type.pros.map((pro, proIndex) => (
                        <li key={proIndex} className="flex items-center text-sm text-gray-600">
                          <span className="text-green-500 mr-2">✓</span>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">Best For:</h4>
                    <p className="text-sm text-gray-600">{type.bestFor}</p>
                  </div>
                  
                  <Link href="/contact" className="bg-yellow-500 text-slate-900 px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors inline-block w-full text-center">
                    Get Quote
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mid-Page Contact Section */}
        <section className="py-16 bg-orange-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Upgrade Your Water Heater?</h2>
            <p className="text-xl text-orange-100 mb-8">
              Get expert advice on the best water heater for your home and budget. Our certified technicians handle everything from selection to installation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <a 
                href={`tel:${phoneNumber}`} 
                className="bg-yellow-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
              >
                🔥 Call: {phoneNumber}
              </a>
              <Link 
                href="/contact" 
                className="bg-white text-orange-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
              >
                📋 Get Free Consultation
              </Link>
            </div>
            <p className="text-orange-200 text-sm">
              ✅ Energy Efficient Options | ✅ Professional Installation | ✅ Extended Warranties
            </p>
          </div>
        </section>

        {/* Replacement Services */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Water Heater Services</h2>
              <p className="text-xl text-gray-600">Complete water heater replacement and installation solutions</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {replacementServices.map((service, index) => (
                <div key={index} className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <div className="space-y-2 mb-4">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <span className="text-green-500 mr-2">✓</span>
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Link href="/contact" className="bg-yellow-500 text-slate-900 px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors inline-block">
                    Get Free Estimate
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Get answers about water heater replacement</p>
            </div>
            
            <div className="space-y-6">
              {faqItems.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready for a New Water Heater?
            </h2>
            <p className="text-xl text-slate-100 mb-8 max-w-3xl mx-auto">
              Upgrade to an energy-efficient water heater and start saving on your utility bills. Our experts will help you choose the perfect system for your home in {serviceArea}.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`tel:${phoneNumber}`} 
                className="bg-yellow-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
              >
                Call Now: {phoneNumber}
              </a>
              <Link 
                href="/contact" 
                className="bg-white text-slate-800 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
              >
                Schedule Consultation
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">{companyName}</h3>
                <p className="text-gray-300 mb-4">Professional plumbing services in {serviceArea}</p>
                <p className="text-gray-300">Licensed & Insured | Emergency Service Available</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/services/water-heater-replacement" className="hover:text-white">Water Heater Replacement</Link></li>
                  <li><Link href="/services/broken-pipe" className="hover:text-white">Broken Pipe Repair</Link></li>
                  <li><Link href="/services/leaky-plumbing" className="hover:text-white">Leak Repair</Link></li>
                  <li><Link href="/services" className="hover:text-white">All Services</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Contact</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>Phone: <a href={`tel:${phoneNumber}`} className="hover:text-white">{phoneNumber}</a></li>
                  <li>Email: <a href="mailto:info@partnersinplumbing.com" className="hover:text-white">info@partnersinplumbing.com</a></li>
                  <li><Link href="/contact" className="hover:text-white">Contact Form</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 {companyName}. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
} 